import React, { useEffect, useRef, useState } from 'react';
import { FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg } from '@/services/staffAttendanceApi';
import { ptApi } from '@/services/ptFilesApi';

const FIELDS = [['company_code', 'Company code', '0001'], ['branch_code', 'Branch code', '00221'], ['party_code', 'Our party code', 'MWT'],
  ['party_name', 'Our party name', 'MINISTER WHITE'], ['voucher_series', 'Voucher series', 'R7230'], ['godown', 'Godown', 'DEC02']];
const blank = Object.fromEntries(FIELDS.map(([k]) => [k, '']));

/** One distributor's codes in their Shoper — typed, or read from one of their old PT files (that also fills design Shoper codes). */
export const PtCodesDialog = ({ customer, onClose, onSaved }) => {
  const [info, setInfo] = useState(null);
  const [codes, setCodes] = useState(blank);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => ptApi.distributor(customer.id).then((d) => { setInfo(d); setCodes({ ...blank, ...(d.codes || {}) }); })
    .catch((e) => toast.error(errMsg(e, 'Could not load the codes')));
  useEffect(() => { if (customer) { setInfo(null); load(); } }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const learn = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await ptApi.learn(customer.id, file);
      toast.success(`Codes read from ${file.name}`, { description: `${r.designs} design code(s), ${r.hsn} HSN name(s) · ${r.added} new, ${r.changed} changed` });
      await load();
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not read that PT file')); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const save = async () => {
    setBusy(true);
    try { await ptApi.saveDistributor(customer.id, codes); toast.success('PT codes saved'); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pt-codes-dialog">
        <DialogHeader>
          <DialogTitle>PT codes · {customer?.name}</DialogTitle>
          <DialogDescription>The codes this distributor's Shoper expects. Easiest: upload one of their old PT files and WMS reads them.</DialogDescription>
        </DialogHeader>
        {!info ? <Loader2 className="w-6 h-6 mx-auto my-8 animate-spin text-amber-600" /> : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-[#F7F2E7] px-3 py-2 text-sm">
              <span>File name code: <b data-testid="pt-codes-file-code">{info.customer.file_code || 'not set'}</b> <span className="text-xs text-gray-500">(Customer Master → Shoper code)</span></span>
              <input ref={fileRef} type="file" accept=".seq,.SEQ,.txt" className="hidden" onChange={(e) => learn(e.target.files?.[0])} data-testid="pt-codes-file-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="pt-codes-learn-btn"><FileUp className="w-4 h-4 mr-1" />Read old PT file</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, label, eg]) => (
                <div key={k} className="space-y-1"><Label className="text-xs">{label}</Label>
                  <Input value={codes[k]} placeholder={`e.g. ${eg}`} onChange={(e) => setCodes({ ...codes, [k]: e.target.value })} data-testid={`pt-codes-${k}`} /></div>
              ))}
            </div>
            {info.codes?.source && <p className="text-xs text-gray-500" data-testid="pt-codes-source">Last: {info.codes.source} · {info.codes.updated_by}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="pt-codes-cancel">Close</Button>
          <Button onClick={save} disabled={busy || !info} className="bg-[#2D5F4F] hover:bg-[#244c3f]" data-testid="pt-codes-save">Save codes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
